// src/components/Sucursales/FormularioSucursal.tsx

import React, { useState } from 'react';
import * as S from './FormularioSucursal.styles'; // Asumimos que este archivo existe y es correcto

import { useForm, Controller, SubmitHandler, FieldErrors, UseFormRegister } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMapPin, FiBriefcase, FiHash, FiSettings, FiSend, FiChevronDown, FiLoader, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import axios, { AxiosError } from 'axios';
import { apiService } from '../../services/apiService';
// --- DEFINICIONES DE TIPOS Y SERVICIOS (AÑADIDOS PARA SER AUTOCONTENIDO) ---

// 1. Tipos para manejar la respuesta de la API
export interface ApiError {
  message: string;
  status?: number;
  details?: any;
}

export interface ServiceResponse<T> {
  data: T | null;
  error: ApiError | null;
}


// --- TIPOS ESPECIÍFICOS DEL FORMULARIO ---

// Tipos para los campos del formulario
type FormValues = {
  area: string;
  numSucursal: string;
  nombreSuc: string;
  tipoSucursal: string;
  ccPlanta: string;
  ccAgrupacion: string;
  calle: string;
  colonia: string;
  municipio: string;
  estado: string;
  codigoPostal: string;
  telefono: string;
  iva: string;
  serieFactura: string;
  impuestos: boolean;
  email: string;
  numExt: string;
  numInt: string;
  pais: string;
  ciudad: string;
  ccSAP: string;
  categoria: string;
  estatusActivo: boolean;
};

// Tipo para el objeto que se envía a la API (payload)
type SucursalPayload = Omit<FormValues, 'area' | 'numSucursal' | 'ccPlanta' | 'ccAgrupacion' | 'iva' | 'pais'> & {
  bajaSucursal: string;
  fechaAlta: string;
  fechaBaja: string | null;
  usuarioDelUltimoCambio: string;
  fechaUltimoCambio: string;
  area: number;
  numSucursal: number;
  ccPlanta: number;
  ccAgrupacion: number;
  iva: number;
  pais: number;
};


// Valores por defecto para el formulario
const defaultValues: FormValues = {
  area: '',
  numSucursal: '',
  nombreSuc: '',
  tipoSucursal: '',
  ccPlanta: '',
  ccAgrupacion: '',
  calle: '',
  colonia: '',
  municipio: '',
  estado: '',
  codigoPostal: '',
  telefono: '',
  iva: '',
  serieFactura: '',
  impuestos: true,
  email: '',
  numExt: '',
  numInt: '',
  pais: '',
  ciudad: '',
  ccSAP: '',
  categoria: '',
  estatusActivo: true,
};

// --- COMPONENTES Y LÓGICA DE LA API ---

// Componente Input reutilizable
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: keyof FormValues;
  label: string;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  icon: React.ReactNode;
}
const Input: React.FC<InputProps> = ({ name, label, register, errors, icon, ...props }) => (
  <S.InputGroup>
    <S.InputLabel htmlFor={name}>{label}</S.InputLabel>
    <S.InputIconWrapper>
      <S.InputIcon>{icon}</S.InputIcon>
      <S.InputField id={name} {...register(name)} {...props} disabled={props.disabled} />
    </S.InputIconWrapper>
    {errors[name] && <S.ErrorText>{errors[name]?.message as string}</S.ErrorText>}
  </S.InputGroup>
);

// Función para manejar y estandarizar los errores de la API
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage = error.response?.data?.message || 'Ocurrió un error en el servidor.';
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: 'Ocurrió un error inesperado. Revisa tu conexión de red.',
  };
};

// Función para crear la sucursal, que devuelve un objeto ServiceResponse
const createSucursal = async (payload: SucursalPayload): Promise<ServiceResponse<SucursalPayload>> => {
  try {
    // Usamos .post y esperamos que la respuesta sea del tipo del payload
    const response = await apiService.post<SucursalPayload, SucursalPayload>('/api/Catalogos/sucursales', payload);
    return { data: response, error: null };
  } catch (err) {
    // Si hay un error, lo procesamos y lo devolvemos en la propiedad 'error'
    return { data: null, error: handleApiError(err) };
  }
};

// --- COMPONENTE PRINCIPAL DEL FORMULARIO ---
const FormularioSucursal = () => {
  const { register, handleSubmit, control, formState: { errors }, reset } = useForm<FormValues>({ defaultValues, mode: 'onBlur' });

  const [openSections, setOpenSections] = useState<string[]>(['general', 'direccion', 'config']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const toggleSection = (sectionId: string) => {
    setOpenSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // onSubmit CORREGIDO para manejar la respuesta del servicio
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const payload: SucursalPayload = {
      ...data,
      bajaSucursal: "No",
      fechaAlta: new Date().toISOString(),
      fechaBaja: null,
      usuarioDelUltimoCambio: "Paola Quintal", // Este valor debería ser dinámico
      fechaUltimoCambio: new Date().toISOString(),
      // Conversión de tipos de string a number
      area: Number(data.area) || 0,
      numSucursal: Number(data.numSucursal) || 0,
      ccPlanta: Number(data.ccPlanta) || 0,
      ccAgrupacion: Number(data.ccAgrupacion) || 0,
      iva: Number(data.iva) || 0,
      pais: Number(data.pais) || 0,
    };

    const response = await createSucursal(payload);

    if (response.error) {
      // Si el objeto de respuesta tiene un error, lo mostramos
      setSubmitStatus('error');
      setSubmitMessage(response.error.message);
      console.error('Fallo en el envío:', response.error);
    } else {
      // Si no hay error, fue un éxito
      setSubmitStatus('success');
      setSubmitMessage('¡Registro enviado con éxito!');
      console.log('Respuesta de la API:', response.data);
      reset(); // Limpiamos el formulario
    }

    setIsSubmitting(false);
  };

  return (
    <S.FormWrapper>
      <S.FormContainer onSubmit={handleSubmit(onSubmit)}>
        <S.Header>
          <S.Title>Nueva sucursal</S.Title>
          <S.Subtitle>Completa todos los campos para dar de alta una nueva entidad en el sistema.</S.Subtitle>
        </S.Header>

        <S.SectionsContainer>
          {/* SECCIÓN INFORMACIÓN GENERAL */}
          <S.FormSection>
            <S.Legend onClick={() => toggleSection('general')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FiBriefcase size={22} /> Información General
              </div>
              <S.ChevronWrapper $isOpen={openSections.includes('general')}>
                <FiChevronDown size={24} />
              </S.ChevronWrapper>
            </S.Legend>
            <AnimatePresence>
              {openSections.includes('general') && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1, marginTop: '1.5rem' }} exit={{ height: 0, opacity: 0, marginTop: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: 'hidden' }}>
                  <S.Grid>
                    <Input name="nombreSuc" label="Nombre Sucursal" register={register} errors={errors} icon={<FiBriefcase size={18} />} disabled={isSubmitting} />
                    <Input name="numSucursal" label="Número de Sucursal" type="number" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    {/*<Input name="tipoSucursal" label="Tipo de Sucursal" register={register} errors={errors} icon={<FiBriefcase size={18} />} disabled={isSubmitting} />
                    <Input name="categoria" label="Categoría" register={register} errors={errors} icon={<FiBriefcase size={18} />} disabled={isSubmitting} />
                    <Input name="area" label="Área" type="number" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />*/}
                  </S.Grid>
                </motion.div>
              )}
            </AnimatePresence>
          </S.FormSection>

          {/* SECCIÓN DIRECCIÓN Y CONTACTO 
          <S.FormSection>
            <S.Legend onClick={() => toggleSection('direccion')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FiMapPin size={22} /> Dirección y Contacto
              </div>
              <S.ChevronWrapper $isOpen={openSections.includes('direccion')}>
                <FiChevronDown size={24} />
              </S.ChevronWrapper>
            </S.Legend>
            <AnimatePresence>
              {openSections.includes('direccion') && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1, marginTop: '1.5rem' }} exit={{ height: 0, opacity: 0, marginTop: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: 'hidden' }}>
                  <S.Grid>
                    <Input name="calle" label="Calle" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="numExt" label="Número Exterior" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="numInt" label="Número Interior (Opcional)" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="colonia" label="Colonia" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="codigoPostal" label="Código Postal" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="municipio" label="Municipio" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="ciudad" label="Ciudad" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="estado" label="Estado" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="pais" label="País (código numérico)" type="number" register={register} errors={errors} icon={<FiMapPin size={18} />} disabled={isSubmitting} />
                    <Input name="telefono" label="Teléfono" type="tel" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="email" label="Email de Contacto" type="email" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                  </S.Grid>
                </motion.div>
              )}
            </AnimatePresence>
          </S.FormSection>*/}

          {/* SECCIÓN CONFIGURACIÓN CONTABLE Y FISCAL 
          <S.FormSection>
            <S.Legend onClick={() => toggleSection('config')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FiSettings size={22} /> Configuración Contable y Fiscal
              </div>
              <S.ChevronWrapper $isOpen={openSections.includes('config')}>
                <FiChevronDown size={24} />
              </S.ChevronWrapper>
            </S.Legend>
            <AnimatePresence>*/}
              {/*{openSections.includes('config') && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1, marginTop: '1.5rem' }} exit={{ height: 0, opacity: 0, marginTop: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: 'hidden' }}>
                  <S.Grid>
                    <Input name="ccPlanta" label="CC Planta" type="number" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="ccAgrupacion" label="CC Agrupación" type="number" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="ccSAP" label="CC SAP" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="iva" label="IVA (%)" type="number" step="0.01" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <Input name="serieFactura" label="Serie de Factura" register={register} errors={errors} icon={<FiHash size={18} />} disabled={isSubmitting} />
                    <S.SwitchGroupContainer>
                      <Controller name="impuestos" control={control} render={({ field: { onChange, value, ref } }) => (
                        <S.SwitchGroup>
                          <S.SwitchToggle>
                            <S.HiddenCheckbox onChange={onChange} ref={ref} checked={value} disabled={isSubmitting} />
                            <S.SwitchSlider checked={value} />
                          </S.SwitchToggle>
                          <S.SwitchLabel>Aplica Impuestos</S.SwitchLabel>
                        </S.SwitchGroup>
                      )} />
                      <Controller name="estatusActivo" control={control} render={({ field: { onChange, value, ref } }) => (
                        <S.SwitchGroup>
                          <S.SwitchToggle>
                            <S.HiddenCheckbox onChange={onChange} ref={ref} checked={value} disabled={isSubmitting} />
                            <S.SwitchSlider checked={value} />
                          </S.SwitchToggle>
                          <S.SwitchLabel>Estatus Activo</S.SwitchLabel>
                        </S.SwitchGroup>
                      )} />
                    </S.SwitchGroupContainer>
                  </S.Grid>
                </motion.div>
              )}
            </AnimatePresence>
          </S.FormSection>*/}
        </S.SectionsContainer>

        <S.ButtonWrapper>
          {submitStatus !== 'idle' && (
            <S.SubmitMessage status={submitStatus}>
              {submitStatus === 'success' ? <FiCheckCircle /> : <FiAlertTriangle />}
              {submitMessage}
            </S.SubmitMessage>
          )}

          <S.SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <S.Spinner><FiLoader /></S.Spinner>
                Enviando...
              </>
            ) : (
              <>
                <FiSend />
                Enviar registro
              </>
            )}
          </S.SubmitButton>
        </S.ButtonWrapper>
      </S.FormContainer>
    </S.FormWrapper>
  );
};

export default FormularioSucursal;
